import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { toBaseResponse, BaseResponse } from './BaseResponse';
import LogUtil from '../utils/LogUtil';
import useAuthStore from '../stores/authStore';
import { API_BASE_URL } from '../config/api';

// axios config에 커스텀 플래그 확장
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retried?: boolean;
    _skipAuthRetry?: boolean;
  }
}

// --- Completer 패턴 (Flutter _refreshCompleter 역할) ---
// 토큰 갱신 중 다른 401 요청을 대기시키기 위한 Promise
let refreshPromise: Promise<boolean> | null = null;

/**
 * 토큰 갱신 (별도 axios 인스턴스 — 인터셉터 무한루프 방지)
 * Flutter _refreshToken() 1:1 대응
 */
async function _refreshToken(): Promise<boolean> {
  try {
    const { refreshToken } = useAuthStore.getState();
    if (!refreshToken) {
      LogUtil.log('[AUTH] refresh token 없음');
      return false;
    }

    // 인터셉터 우회를 위해 별도 인스턴스 사용 (Flutter refreshDio 동일)
    const refreshClient = axios.create();
    const response = await refreshClient.post(
      `${API_BASE_URL}/pharmacy/auth/refresh`,
      { refresh_token: refreshToken }
    );

    if (response.status === 200 && response.data) {
      const data = response.data;
      const newAccessToken = data.data?.access_token ?? data.access_token;
      const newRefreshToken = data.data?.refresh_token ?? data.refresh_token;

      if (newAccessToken) {
        useAuthStore
          .getState()
          .setTokens(newAccessToken, newRefreshToken || refreshToken);
        LogUtil.log('[AUTH] 새 토큰 저장 완료');
        return true;
      }
    }

    return false;
  } catch (e) {
    LogUtil.error('[AUTH] 토큰 갱신 API 오류:', e);
    return false;
  }
}

/**
 * 원래 요청 재시도 (새 토큰 적용)
 * Flutter _retryRequest() 1:1 대응
 */
function _retryRequest(
  client: AxiosInstance,
  config: AxiosRequestConfig & { _retried?: boolean }
): Promise<AxiosResponse> {
  const newToken = useAuthStore.getState().accessToken;
  config.headers = config.headers || {};
  (config.headers as Record<string, string>)['Authorization'] =
    `Bearer ${newToken}`;
  config._retried = true;
  return client.request(config);
}

/**
 * 토큰 삭제 및 로그아웃 처리
 * Flutter _clearTokensAndLogout() 대응
 */
function _forceLogout(): void {
  useAuthStore.setState({
    pharmacy: null,
    accessToken: null,
    refreshToken: null,
    isDefaultPassword: false,
  });
  window.location.href = '/login';
}

/**
 * HTTP API 클라이언트
 * 실제 HTTP 통신을 담당하는 인프라스트럭처 컴포넌트
 */
export class ApiClient {
  private client: AxiosInstance;

  /**
   * ApiClient 생성자
   * @param baseUrl - API 기본 URL
   * @param options - 추가 옵션
   */
  constructor(
    baseUrl: string,
    options: { timeout?: number; headers?: Record<string, string> } = {}
  ) {
    this.client = axios.create({
      baseURL: baseUrl,
      timeout: options.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // 요청 인터셉터 — Authorization 헤더 자동 주입
    this.client.interceptors.request.use(
      config => {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        LogUtil.log(
          `📤 [API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`
        );
        return config;
      },
      error => Promise.reject(error)
    );

    // 응답 인터셉터 — 401 Completer 패턴 (Flutter onError 1:1 대응)
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        LogUtil.log(
          `📥 [API Response] ${response.status} ${response.config.url}`
        );
        LogUtil.log('📦 [API Response] Raw data:', response.data);

        // API 응답이 이미 BaseResponse 형태인 경우 그대로 반환
        if (
          response.data &&
          typeof response.data === 'object' &&
          ('result_code' in response.data || 'resultCode' in response.data)
        ) {
          LogUtil.log('✅ [API Response] BaseResponse 형태 응답 감지');
          return response.data;
        }

        // 일반 응답인 경우 BaseResponse로 래핑
        LogUtil.log('🔄 [API Response] 일반 응답을 BaseResponse로 래핑');
        const baseResponse = toBaseResponse({
          resultCode: response.status,
          resultMsg: 'Success',
          data: response.data,
          success: true,
          errors: [],
        });

        return baseResponse;
      },
      async error => {
        const config = error.config;
        const isStatus401 = error.response?.status === 401;
        const isRetried = config?._retried === true;
        const skipAuthRetry = config?._skipAuthRetry === true;

        LogUtil.error(`❌ [API Error] ${config?.url}:`, error.message);

        // 폴링 등 _skipAuthRetry 요청은 재시도 없이 바로 reject
        if (isStatus401 && skipAuthRetry) {
          return Promise.reject(error);
        }

        // 401 에러 + 재시도가 아닌 요청 → 토큰 갱신 대상 (Completer 패턴)
        if (isStatus401 && !isRetried) {
          try {
            // 갱신 중이 아니면 → 갱신 시작
            if (refreshPromise === null) {
              refreshPromise = _refreshToken();
              const refreshed = await refreshPromise;
              refreshPromise = null;

              if (refreshed) {
                LogUtil.log('[AUTH] 토큰 갱신 성공, 원래 요청 재시도');
                return _retryRequest(this.client, config);
              } else {
                LogUtil.log('[AUTH] 토큰 갱신 실패, 로그아웃 처리');
                _forceLogout();
                return Promise.reject(error);
              }
            }
            // 갱신 중이면 → 완료 대기 후 재시도 (Flutter else 분기 동일)
            else {
              const refreshed = await refreshPromise;

              if (refreshed) {
                return _retryRequest(this.client, config);
              } else {
                return Promise.reject(error);
              }
            }
          } catch (e) {
            LogUtil.error('[AUTH] 토큰 갱신 중 오류:', e);
            refreshPromise = null;
            _forceLogout();
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET 요청
   */
  async get<T>(
    url: string,
    config: AxiosRequestConfig = {}
  ): Promise<BaseResponse<T>> {
    const response = await this.client.get(url, config);
    return response as unknown as BaseResponse<T>;
  }

  /**
   * POST 요청
   */
  async post<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {}
  ): Promise<BaseResponse<T>> {
    const response = await this.client.post(url, data, config);
    return response as unknown as BaseResponse<T>;
  }

  /**
   * PUT 요청
   */
  async put<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {}
  ): Promise<BaseResponse<T>> {
    const response = await this.client.put(url, data, config);
    return response as unknown as BaseResponse<T>;
  }

  /**
   * DELETE 요청
   */
  async delete<T>(
    url: string,
    config: AxiosRequestConfig = {}
  ): Promise<BaseResponse<T>> {
    const response = await this.client.delete(url, config);
    return response as unknown as BaseResponse<T>;
  }

  /**
   * PATCH 요청
   */
  async patch<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {}
  ): Promise<BaseResponse<T>> {
    const response = await this.client.patch(url, data, config);
    return response as unknown as BaseResponse<T>;
  }
}
